import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { OptionsAssetIcon } from './OptionsAssetIcon';
import { OptionsMonetaryText } from './OptionsMonetaryText';
import { useOptionsLocale } from '../../context/OptionsLanguageContext';

export const OptionsDashboardTradingPerformanceCard = ({ model, periodControls }) => {
  const navigate = useNavigate();
  const { isPortuguese } = useOptionsLocale();

  const labelTradingPnl = isPortuguese ? 'TRADING P&L (LÍQUIDO)' : 'TRADING P&L (NET)';
  const labelResultOnCapital = isPortuguese ? 'Resultado / Capital Inicial' : 'Result / Starting Capital';
  const labelDisclaimer = isPortuguese
    ? 'Indicador simples baseado no resultado realizado líquido e no capital inicial do período. Não ajusta depósitos ou saques ocorridos durante o período.'
    : 'Simple indicator based on realized result and starting capital for the period. It does not adjust for deposits or withdrawals.';
  const labelLink = isPortuguese ? 'Ver retorno por períodos →' : 'View return by capital periods →';

  const formatDateComplement = (ms) => {
    if (!ms) return '';
    const d = new Date(ms);
    // Usa pt-BR ou en-US para formatar
    const formatted = d.toLocaleDateString(isPortuguese ? 'pt-BR' : 'en-US', { day: 'numeric', month: 'short', year: 'numeric' });
    return isPortuguese
      ? `Calculado desde ${formatted}, início da atividade de Options.`
      : `Calculated since ${formatted}, options trading start.`;
  };

  return (
    <Card className="border-white/10 bg-white/[0.03] text-white shadow-none" data-testid="options-dashboard-trading-performance-card">
      <CardHeader className="space-y-5 pb-4">
        <div className="flex flex-col gap-5 xl:flex-row xl:items-start xl:justify-between">
          <div className="space-y-4">
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-white/45" data-typography="label" data-testid="options-dashboard-trading-performance-label">
              {model.sectionLabel}
            </p>
          </div>

          <div className="flex w-full flex-col gap-4 xl:max-w-[460px] xl:items-end" data-testid="options-dashboard-trading-actions-column">
            <div className="w-full" data-testid="options-dashboard-trading-period-controls-slot">
              {periodControls}
            </div>
          </div>
        </div>
      </CardHeader>

      <CardContent>
        <div className="grid gap-6 xl:grid-cols-[1.25fr,0.75fr]" data-testid="options-dashboard-trading-summary-grid">
          
          {/* LADO ESQUERDO: TRADING PERFORMANCE */}
          <div className="space-y-4">
            
            {/* TRADING P&L PRINCIPAL */}
            <div className="rounded-[24px] border border-white/10 bg-black/20 p-5" data-testid="options-dashboard-trading-pnl-block">
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-white/40" data-typography="label">
                {labelTradingPnl}
              </p>
              <p className={`mt-2 text-3xl font-black tracking-tight sm:text-4xl ${model.tradingPnlToneClass}`} data-typography="metric" data-testid="options-dashboard-trading-pnl-value">
                <OptionsMonetaryText value={model.tradingPnlHeadline} iconSize={20} />
              </p>
              {model.tradingPnlHeadlineSecondary ? (
                <p className="mt-1 text-sm text-white/55 tabular-nums" data-testid="options-dashboard-trading-pnl-secondary">
                  <OptionsMonetaryText value={model.tradingPnlHeadlineSecondary} iconSize={14} />
                </p>
              ) : null}
            </div>

            {/* REALIZADO, NÃO REALIZADO, TAXAS */}
            <div className="grid gap-3 sm:grid-cols-3" data-testid="options-dashboard-trading-breakdown-grid">
              {model.breakdown.map((item) => (
                <div key={item.key} className="rounded-2xl border border-white/10 bg-black/20 p-4" data-testid={`options-dashboard-trading-breakdown-${item.key}`}>
                  <p className="text-[11px] uppercase tracking-[0.18em] text-white/40" data-typography="label">{item.label}</p>
                  <p className={`mt-2 text-base font-bold ${item.toneClass}`} data-typography="metric" data-testid={`options-dashboard-trading-breakdown-${item.key}-primary`}>
                    <OptionsMonetaryText value={item.primary} iconSize={15} />
                  </p>
                  {item.secondary ? (
                    <p className="mt-1 text-xs text-white/55 tabular-nums" data-testid={`options-dashboard-trading-breakdown-${item.key}-secondary`}>
                      <OptionsMonetaryText value={item.secondary} iconSize={13} />
                    </p>
                  ) : null}
                </div>
              ))}
            </div>

            {/* RESULTADO SOBRE CAPITAL INICIAL */}
            <div className="rounded-[24px] border border-white/10 bg-black/20 p-5 space-y-3" data-testid="options-dashboard-result-capital-block">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-white/40" data-typography="label">
                    {labelResultOnCapital}
                  </p>
                  <p className={`mt-1.5 text-2xl font-black ${model.resultOnStartingCapitalToneClass}`} data-typography="metric" data-testid="options-dashboard-result-capital-value">
                    {model.resultOnStartingCapitalFormatted}
                  </p>
                </div>
                
                {/* Botão de navegação */}
                <button
                  type="button"
                  onClick={() => navigate('/options/performance')}
                  className="self-start text-xs font-semibold text-[#60A5FA] hover:text-[#93C5FD] transition-colors sm:self-center"
                  data-testid="options-dashboard-performance-link"
                >
                  {labelLink}
                </button>
              </div>
              <div className="space-y-1" data-testid="options-dashboard-result-capital-disclaimer-container">
                <p className="text-xs text-white/45 leading-normal" data-testid="options-dashboard-result-capital-disclaimer">
                  {labelDisclaimer}
                </p>
                {model.isOperationalLimited && model.earliestExposureMs && (
                  <p className="text-[11px] text-white/35 font-semibold leading-normal" data-testid="options-dashboard-result-capital-operational-note">
                    {formatDateComplement(model.earliestExposureMs)}
                  </p>
                )}
              </div>
            </div>

          </div>

          {/* LADO DIREITO: RESULTADO POR ATIVO */}
          <div className="rounded-[28px] border border-white/10 bg-black/20 p-5 flex flex-col" data-testid="options-dashboard-trading-by-asset-card">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-white/40 mb-1" data-typography="label" data-testid="options-dashboard-trading-by-asset-label">
              {model.byAssetLabel}
            </p>

            {model.byAssetRows.length ? (
              <div className="mt-3 space-y-3 flex-1 overflow-y-auto" data-testid="options-dashboard-trading-by-asset-list">
                {model.byAssetRows.map((row) => (
                  <div
                    key={row.key}
                    className="flex flex-col gap-3 rounded-2xl border border-white/10 bg-white/[0.03] p-4 sm:flex-row sm:items-center sm:justify-between"
                    data-testid={`options-dashboard-trading-by-asset-row-${row.key.toLowerCase()}`}
                  >
                    <div className="flex items-center gap-3" data-testid={`options-dashboard-trading-by-asset-meta-${row.key.toLowerCase()}`}>
                      <OptionsAssetIcon assetKey={row.currency} size={18} className="shrink-0" />
                      <div>
                        <p className="text-sm font-semibold text-white">{row.code}</p>
                        <p className="text-xs text-white/45">{row.name}</p>
                      </div>
                    </div>

                    <div className="sm:text-right" data-testid={`options-dashboard-trading-by-asset-values-${row.key.toLowerCase()}`}>
                      <p className={`text-base font-semibold tabular-nums ${row.toneClass}`} data-typography="metric">{row.primary}</p>
                      {row.secondary ? <p className="mt-1 text-sm text-white/55 tabular-nums">{row.secondary}</p> : null}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="mt-4 text-sm text-white/55" data-testid="options-dashboard-trading-by-asset-empty">{model.emptyByAssetLabel}</p>
            )}
          </div>

        </div>
      </CardContent>
    </Card>
  );
};

export default OptionsDashboardTradingPerformanceCard;